/* ---------------------------------------------------------------------------
   The four applications, and the home page in front of them.

   This deployment is a SITE, not an application. Bridge Inspection, Bridge
   Rating, Construction Scheduler and Construction Monitoring are four products
   over one register, one sign-in and one set of tenants; the home page tabulates
   the structures and hands you off to whichever of them the job belongs to.

   ══ ONE DOCUMENT, FOUR APPLICATIONS ══

   This used to be one HTML entry per app. It is now ONE page — index.html — with
   a start page and an app page, and the app is part of the address:
   `#/scheduler/schedule/BR-…` names a bridge's programme exactly as
   `scheduler.html#/schedule/BR-…` used to, so a link can still be bookmarked,
   mailed and opened by somebody who has no business with inspections. What
   changed is that moving between apps is a repaint rather than a page load: the
   ribbon's app tabs (src/components/ribbon.ts) are four tabs over one register.

   The old documents are kept as one-line redirects to the new addresses, so a
   link somebody sent last month still arrives — see inspection.html.

   `running` below is the app on screen. The router sets it from the address (see
   src/core/router.ts) and `actions.openApp` sets it from a click; `State.app`
   mirrors it so that a change of app repaints like any other change.

   ══ WHAT THEY SHARE, AND IT IS ALMOST EVERYTHING ══

   Three of the four are ONE STORE — src/main.ts, src/core/store.ts, one register,
   one queue, one service worker — rendered with a different ribbon and a
   different set of screens. `currentApp()` below is the whole of the difference.
   That is deliberate: the
   alternative is four stores over one D1, which is four copies of hydrate, four
   offline queues and four chances for the register on screen to disagree with
   itself.

   Bridge Rating is the exception and stays the exception. It has its own store
   because a rating is a long form over an engine version, and it was built
   before this split; `separate: true` marks it. It is mounted into the same page
   (src/rating/mount.ts) but keeps its store and its four screens to itself.

   ══ THE RULE FOR ADDING A SCREEN ══

   A screen belongs to exactly one app for NAVIGATION, and `screens` below is
   where that is written. `list` is the exception and is deliberate — the
   register is how you pick a structure, so three apps carry it and each opens a
   different screen from a row (see `structure`).
   --------------------------------------------------------------------------- */

import { ICONS, type IconPath } from './dom.ts';
import type { DetailView, Screen, Shell } from '../data/types.ts';

export type AppId = 'home' | 'structure' | 'inspection' | 'rating' | 'scheduler' | 'monitoring';

export interface AppDef {
  id: AppId;
  /** The product name, as the home card and the rail print it. */
  label: string;
  /**
   * The name over the workspace, in the title bar and at the head of the nav
   * pane.
   *
   * The same as `label` for the four products, and NOT the same for home: a
   * title bar reading "Home" names a destination where it should name the thing
   * you are in, and what you are in there is the register itself. So the home
   * page wears the product family's name and the rail still says "Home", which
   * is what a way back is called.
   */
  brand: string;
  /** The short form, for the rail down the side of the nav pane. */
  short: string;
  /** One line on the home card: what this app is for, in a reader's words. */
  blurb: string;
  /**
   * The document it is served from, relative so a preview deployment works.
   * One page for all of them now; kept as a field because it is what a link
   * from outside the running page (an email, the manifest) is built on.
   */
  page: string;
  iconPath: IconPath;
  /**
   * The screens it owns, in nav order. Empty for an app with its own build,
   * whose screens this build knows nothing about.
   */
  screens: readonly Screen[];
  /** Where it opens when nothing in the address says otherwise. */
  landing: Screen;
  /**
   * Where "open this structure" lands inside this app. The 3D model in every
   * product but the Scheduler, which lands on its programme: the explorer and
   * the property panel carry the structure's record beside it, and the app's
   * own screens are a ribbon command away.
   *
   * Null on home, which is a page about the register as a whole and hands a
   * structure to an app rather than opening one itself, and on Rating, whose
   * structure screen belongs to a build this one cannot route into.
   */
  structure: Screen | null;
  /** True where the app is its own build with its own store. */
  separate?: boolean;
}

/**
 * The site, in the order the rail draws it.
 *
 * Home first because it is where a session starts. Then the four, in the order
 * a structure meets them: it is inspected, it is rated, a programme is made for
 * it, and the work against that programme is recorded.
 */
export const APPS: readonly AppDef[] = [
  {
    id: 'home',
    brand: 'Bridge Watch',
    label: 'Home',
    short: 'Home',
    blurb: 'Every structure on file, summarised, with the way in to each application.',
    page: './index.html',
    iconPath: ICONS.grid,
    /* People is here rather than in an app because it administers the SITE —
       one role per person across all four — and the guide describes the site
       for the same reason. Both are reachable from every app's nav all the
       same; see `commonNav`. */
    /* And one structure's dashboard (LC-30), which is every application's
       answer about it at once and so belongs to none of them. */
    /* The network summary too: it is the register as a document, about every
       structure and none in particular, which is what this page is. */
    screens: ['dashboard', 'people', 'guide', 'summary', 'board'],
    landing: 'dashboard',
    structure: null,
  },
  {
    id: 'inspection',
    brand: 'Bridge Inspection',
    label: 'Bridge Inspection',
    short: 'In-service Inspection',
    blurb:
      'The condition of a structure: inspections, issues, remedial measures and the inventory sheet.',
    page: './index.html',
    iconPath: ICONS.inspect,
    screens: ['list', 'detail', 'network', 'report', 'sheet', 'options', 'guide'],
    landing: 'detail',
    structure: 'detail',
  },
  {
    id: 'rating',
    brand: 'Bridge Rating',
    label: 'Bridge Rating',
    short: 'In-service Rating',
    blurb: 'What a structure may carry: load rating against the department’s method, and posting.',
    page: './index.html',
    iconPath: ICONS.scale,
    /* Its own build, its own store, its own four screens. This one cannot name
       them and must not try — see the header. The one screen it does share is
       the 3D model, which every app shows in its content panel. */
    screens: ['detail'],
    landing: 'detail',
    structure: 'detail',
    separate: true,
  },
  {
    id: 'scheduler',
    brand: 'Construction Scheduler',
    label: 'Construction Scheduler',
    short: 'Construction Scheduler',
    blurb: 'The programme for a structure: the baseline, what has been matched to it, and the variance.',
    page: './index.html',
    iconPath: ICONS.gantt,
    /* Opens on the programme's Gantt rather than the 3D model: the programme
       is what the Scheduler is for. */
    screens: ['list', 'schedule', 'detail', 'guide'],
    landing: 'schedule',
    structure: 'schedule',
  },
  {
    id: 'monitoring',
    brand: 'Construction Monitoring',
    label: 'Construction Monitoring',
    short: 'Construction Reporting',
    blurb: 'What was built and when: site entries, daily returns, incidents, and the period report.',
    page: './index.html',
    iconPath: ICONS.hardhat,
    screens: ['list', 'detail', 'site', 'works', 'guide'],
    landing: 'detail',
    structure: 'detail',
  },
  /* What the structure is and where it is in its life — Bridge info and State —
     which every application used to repeat at the head of its explorer. One
     place for them now, with the administrator's reference tables under them.
     Not a product: no home card, no row on the dashboard (see `PRODUCTS`).
     Last here although first in the ribbon (`RIBBON_APPS`), so that an address
     with no app in it — `#/bridge/<id>` — still means Bridge Inspection's
     record, and not `bridge`, which that address already spells. */
  {
    id: 'structure',
    brand: 'Bridge',
    label: 'Bridge',
    short: 'Bridge model',
    blurb: 'What a structure is: its inventory, its state, and the reference tables.',
    page: './index.html',
    iconPath: ICONS.book,
    screens: ['detail'],
    landing: 'detail',
    structure: 'detail',
  },
];

/**
 * The property-panel pages each application's explorer offers, Bridge info
 * aside, which an address names as `?page=` (LC-31). Monitoring's are not here:
 * its pages are the site record's and open in the content panel, so they are
 * the path — `#/site/<id>/charts`. The explorers themselves are drawn in
 * src/screens/detail.ts, and a check holds the two to one list.
 */
export const EXPLORER_PAGES: Readonly<Record<AppId, readonly DetailView[]>> = {
  home: [],
  structure: ['state'],
  inspection: ['inspections', 'condition', 'defects'],
  rating: ['rating', 'ratingHistory'],
  scheduler: ['baseline', 'variance'],
  monitoring: [],
};

/** One app by id. Total, because every caller here has an id from this file. */
export function appById(id: AppId): AppDef {
  const found = APPS.find((a) => a.id === id);
  /* Not a soft fallback. An unknown id can only come from a typo in this
     repository or a hand-edited `data-app`, and quietly serving Home for it
     would hide the first and reward the second. */
  if (!found) throw new Error(`no such app: ${id}`);
  return found;
}

/* --------------------------- which app is running --------------------------- */

/**
 * The app id a document declares, or null where none does.
 *
 * `data-app` on `<html>`, set in each entry's HTML. Read from the document
 * rather than injected at build time so that all three shared apps are ONE
 * bundle: the same main.ts is the entry for inspection.html, scheduler.html and
 * monitoring.html, which means one hashed chunk in the cache and one download
 * for a device that uses two of them.
 */
function declaredApp(): AppId | null {
  if (typeof document === 'undefined') return null;
  const raw = document.documentElement.dataset['app'];
  return APPS.some((a) => a.id === raw) ? (raw as AppId) : null;
}

let override: AppId | null = null;

/** The app on screen, as the router or a ribbon tab last set it. */
let running: AppId | null = null;

/**
 * Put an app on screen. Called by the router when an address names one and by
 * `actions.openApp`; nothing else should, because `State.app` has to be moved
 * in the same breath or the next paint draws one app's ribbon over another's
 * screen.
 */
export function setRunningApp(id: AppId): void {
  running = id;
}

/**
 * Which app this document is.
 *
 * Falls back to Bridge Inspection rather than Home, and that is the safer of the
 * two defaults: Inspection is the app that owns the bridge record, so a document
 * that forgot to declare itself renders a working register rather than a home
 * page whose four cards all point away from it.
 */
export function currentApp(): AppDef {
  return appById(override ?? running ?? declaredApp() ?? 'inspection');
}

/**
 * Force the running app, for checks.
 *
 * `npm test` renders every screen of every app in one process, which no browser
 * ever does. Nothing in the application calls this: a document declares what it
 * is in its own HTML.
 */
export function setCurrentApp(id: AppId | null): void {
  override = id;
}

/**
 * The app actually in force, which on a field device is always Inspection.
 *
 * THE FIELD SHELL IS ONE APP AND CANNOT BE FOUR. An inspector holds a phone at a
 * structure with no signal; a rail that offered to load three more documents
 * would be offering three more chances to get a blank page in a gorge, and two
 * of those documents are desk work besides — a period report and a programme are
 * read in an office. So a field device runs the inspection app whichever
 * document it was opened from, which is also what keeps the installed app
 * working: the manifest's start_url is index.html, home is a desk page, and an
 * inspector who taps the icon lands on the register either way.
 *
 * Takes the shell rather than reading state, so the rule is a pure function a
 * check can drive — the same argument src/core/shell.ts makes about chooseShell.
 */
export function appForShell(shell: Shell): AppDef {
  return shell === 'field' ? appById('inspection') : currentApp();
}

/**
 * Does anything this device is going to draw read a structure's site record?
 *
 * SIX REQUESTS PER SELECTED STRUCTURE hang off this — entries, daily returns,
 * incidents, documents, baseline and schedule matches, settled as one in
 * `refreshProgress`. They used to be made on every application, because the
 * bridge record carried the Works and Programme cards and the register's row
 * click moves the selection; with those cards gone from the desk record, Bridge
 * Inspection at a desk would have been making all six on every row click for
 * nothing on screen.
 *
 * TRUE ON THE FIELD SHELL WHATEVER THE APPLICATION, and that is not a special
 * case but the same rule: the field record still carries the Works card, because
 * recording a pour happens at the pour and Construction Monitoring has no field
 * shell of its own. So the question this answers is "will something draw it",
 * not "which product is this".
 */
export function readsSiteRecord(shell: Shell, screen?: Screen): boolean {
  if (shell === 'field') return true;
  /* The bridge dashboard (LC-30) draws the programme and the site's open items
     from the start page, which owns neither. */
  if (screen === 'board') return true;
  const app = appForShell(shell);
  return owns(app, 'site') || owns(app, 'schedule') || owns(app, 'works');
}

/* --------------------------- routing between them --------------------------- */

/** Does this app draw that screen? */
export function owns(app: AppDef, screen: Screen): boolean {
  return app.screens.includes(screen);
}

/**
 * The app a screen belongs to, preferring the one already running.
 *
 * The preference is what makes `list` work: three apps carry the register, and
 * "go to the register" means the one in the app you are in, not the first in
 * the list. Null for a screen no app claims, which cannot happen for a Screen
 * this build knows — it is here so a screen added without a home is a visible
 * null rather than a silent redirect to Inspection.
 */
export function appOf(screen: Screen, from: AppDef = currentApp()): AppDef | null {
  if (owns(from, screen)) return from;
  return APPS.find((a) => owns(a, screen)) ?? null;
}

/**
 * The address of a screen in an app, for a link that may leave this page: the
 * app's document plus the hash. Within the running page the hash alone is the
 * link — see src/core/navigate.ts — and this is for the places that need the
 * whole address.
 *
 * The hash is the same string this build already encodes for that screen (see
 * src/core/router.ts), which names the app itself, so a link across apps
 * carries the structure, the period or the filters exactly as a link within
 * one does.
 */
export function pageHref(app: AppDef, hash: string): string {
  return `${app.page}${hash.startsWith('#') ? hash : `#${hash}`}`;
}

/** The four products: everything but the start page and Bridge. */
export const PRODUCTS: readonly AppDef[] = APPS.filter((a) => a.id !== 'home' && a.id !== 'structure');

/**
 * The ribbon's tabs between File and View: Bridge, then the four products —
 * the construction pair first, because a structure meets them first, then
 * Inspection and Rating, which open once it is in service.
 */
export const RIBBON_APPS: readonly AppDef[] = (['structure', 'scheduler', 'monitoring', 'inspection', 'rating'] as const).map(appById);

/** Is this string an app id? For the router, which reads one off an address. */
export function isAppId(raw: string | undefined): raw is AppId {
  return APPS.some((a) => a.id === raw);
}
